import React, { useEffect, useState, useRef } from "react";
import {
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Autocomplete,
} from "@mui/material";
import readStreamAsBase64 from "../utils/readStreamAsBase64";
import { AddPhotoAlternate } from "@mui/icons-material";
import resizeFile from "../utils/resizeFile";
import CustomMap from "./CustomMap";
import config from "../config";

const genres = [
  "Dance & Electronic",
  "Pop",
  "Rap/Hip-Hop",
  "Rock",
  "Alternative Rock",
  "Jazz",
  "Pop Folk",
  "Lounge music",
];

const currencies = [
  {
    label: "€ (EUR)",
    value: "EUR",
  },
  {
    label: "$ (USD)",
    value: "USD",
  },
];

// const countries = [
//   {
//     label: "Bulgaria (BG)",
//     value: "Bulgaria",
//   },
//   {
//     label: "Greece (GR)",
//     value: "Greece",
//   },
//   {
//     label: "Netherlands (NL)",
//     value: "Netherlands",
//   },
//   {
//     label: "Philippines (PH)",
//     value: "Philippines",
//   },
//   {
//     label: "Portugal (PT)",
//     value: "Portugal",
//   },
//   {
//     label: "United States (US)",
//     value: "United States",
//   },
// ];

export default function VenueModal(props) {
  const nameInputRef = useRef();
  const facebookInputRef = useRef();
  const instagramInputRef = useRef();
  const websiteInputRef = useRef();
  const cityInputRef = useRef();
  const addressInputRef = useRef();

  const [state, setState] = useState({
    genres: [],
    currency: "EUR",
  });

  useEffect(() => {
    setState((s) => ({
      ...s,
      ...props.venue,
      genres: props.venue?.shortDescription?.split(" · ") || [],
    }));
  }, [props.venue]);

  const handleCurrencyChange = (e, value) => {
    setState((s) => {
      return {
        ...s,
        currency: value.value,
      };
    });
  };

  const handleChangeLogo = async (e) => {
    const logo = await resizeFile(e.target.files[0]);
    setState((s) => ({
      ...s,
      logo,
      logoFile: URL.createObjectURL(e.target.files[0]),
      logoHasChanged: true,
    }));
  };

  const handleConfirm = async () => {
    if (!nameInputRef.current.value) {
      setState((s) => ({
        ...s,
        errors: {
          name: "Name is required.",
        },
      }));
      return;
    }

    setState((s) => ({
      ...s,
      isLoading: true,
      errors: undefined,
    }));

    const base64Logo = state.logoHasChanged
      ? await readStreamAsBase64(state.logo)
      : undefined;

    const countryResult = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${state.coords.latitude},${state.coords.longitude}&key=AIzaSyAgSDkBpMqDPZdvgaimA3tIFLVUa5xDMM8`
    );
    const countryJson = await countryResult.json();
    const { long_name, short_name } = countryJson.results
      .find((x) => x.types.includes("country"))
      .address_components.find((x) => x.types.includes("country"));

    const body = {
      id: state.id,
      name: nameInputRef.current.value,
      facebook: facebookInputRef.current.value,
      instagram: instagramInputRef.current.value,
      website: websiteInputRef.current.value,
      city: cityInputRef.current.value,
      address: addressInputRef.current.value,
      shortDescription: genres
        .filter((x) => state.genres.includes(x))
        .join(" · "),
      coords: state.coords,
      currency: state.currency || "EUR",
      base64Logo,
      country: long_name,
      countryCode: short_name,
    };
    await props.handleConfirm(body);
    props.handleClose();
  };

  const handleGenresChange = (genre) => (e) => {
    if (e.target.checked) {
      setState((s) => ({
        ...s,
        genres: [...s.genres, genre],
      }));
    } else {
      setState((s) => ({
        ...s,
        genres: s.genres.filter((x) => x !== genre),
      }));
    }
  };

  const handleCoordsChange = (coords) => {
    setState((s) => ({
      ...s,
      coords,
    }));
  };

  return (
    <Dialog
      open={true}
      onClose={props.handleClose}
      PaperProps={{
        style: {
          backgroundColor: "#d8c8f4",
          color: "#4b445b",
        },
      }}
    >
      <DialogTitle>{state.id ? "Edit Venue" : "Create Venue"}</DialogTitle>
      <DialogContent>
        <TextField
          autoComplete="off"
          margin="dense"
          id="name"
          label="Name"
          type="text"
          fullWidth
          variant="outlined"
          inputRef={nameInputRef}
          defaultValue={state.name}
          required
          error={!!state.errors?.name}
          helperText={state.errors?.name}
        />
        <TextField
          autoComplete="off"
          margin="dense"
          variant="outlined"
          fullWidth
          label="Logo"
          multiline
          InputLabelProps={{ shrink: true }}
          InputProps={{
            inputComponent: "div",
          }}
          inputProps={{
            children: (
              <>
                {(state.logoFile || state.id) && (
                  <img
                    alt={state.name}
                    src={state.logoFile || `${config.cdnUrl}/logos/${state.id}`}
                    height="200"
                    style={{ borderRadius: "8px" }}
                  />
                )}
                <div>
                  <Button
                    variant="outlined"
                    component="label"
                    sx={{ marginTop: "4px" }}
                  >
                    <AddPhotoAlternate
                      sx={{ fontSize: "20px", marginRight: "4px" }}
                    />
                    {state.id ? "Change" : "Add"}
                    <input type="file" hidden onChange={handleChangeLogo} />
                  </Button>
                </div>
              </>
            ),
          }}
        />
        <TextField
          autoComplete="off"
          margin="dense"
          id="city"
          label="City"
          type="text"
          fullWidth
          variant="outlined"
          inputRef={cityInputRef}
          defaultValue={state.city}
        />
        <TextField
          autoComplete="off"
          margin="dense"
          id="address"
          label="Address"
          type="text"
          fullWidth
          variant="outlined"
          inputRef={addressInputRef}
          defaultValue={state.address}
        />
        <TextField
          autoComplete="off"
          margin="dense"
          id="facebook"
          label="Facebook Link"
          type="text"
          fullWidth
          variant="outlined"
          inputRef={facebookInputRef}
          defaultValue={state.facebook}
        />
        <TextField
          autoComplete="off"
          margin="dense"
          id="instagram"
          label="Instagram Link"
          type="text"
          fullWidth
          variant="outlined"
          inputRef={instagramInputRef}
          defaultValue={state.instagram}
        />
        <TextField
          autoComplete="off"
          margin="dense"
          id="website"
          label="Website Link"
          type="text"
          fullWidth
          variant="outlined"
          inputRef={websiteInputRef}
          defaultValue={state.website}
        />
        <TextField
          autoComplete="off"
          margin="dense"
          variant="outlined"
          fullWidth
          label="Genres"
          multiline
          InputLabelProps={{ shrink: true }}
          InputProps={{
            inputComponent: "div",
          }}
          inputProps={{
            children: (
              <>
                {genres.map((genre) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={state.genres.includes(genre)}
                        onChange={handleGenresChange(genre)}
                      />
                    }
                    label={genre}
                    key={genre}
                  />
                ))}
              </>
            ),
          }}
        />
        <CustomMap
          zoom={state.coords ? 15 : 10}
          center={
            state.coords
              ? {
                  lat: state.coords.latitude,
                  lng: state.coords.longitude,
                }
              : {
                  lat: 42.6977,
                  lng: 23.3219,
                }
          }
          handleCoordsChange={handleCoordsChange}
        />
        <Autocomplete
          disablePortal
          id="currency-dropdown"
          options={currencies}
          value={currencies.find((x) => x.value === state.currency)}
          disableClearable
          onChange={handleCurrencyChange}
          renderInput={(params) => (
            <TextField
              margin="dense"
              fullWidth
              variant="outlined"
              label="Currency"
              id="currency"
              type="text"
              {...params}
            />
          )}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={props.handleClose} variant="contained">
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={state.isLoading}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
}
